import { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, addDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { advanceDealStage, denyDeal } from '../../services/dealService';
import { sendInternalNotification } from '../../services/pifService';
import { useAuth } from '../../hooks/useAuth';
import { formatPercentage, formatDateTime, getFriendlyError } from '../../utils/formatters';
import Button from '../shared/UI/Button';
import ToggleField from '../shared/Forms/ToggleField';
import SelectField from '../shared/Forms/SelectField';
import FormField from '../shared/Forms/FormField';

export default function AnalystReviewForm({ deal, fund, reviewType = 'primary_analyst', onSaved }) {
  const { user, userProfile } = useAuth();
  const [analysts, setAnalysts] = useState([]);
  const [form, setForm] = useState({
    notes: '',
    sector_concentration_flag: false,
    sector_concentration_notes: '',
    obligor_exposure_flag: false,
    obligor_exposure_notes: '',
    dry_powder_flag: false,
    dry_powder_notes: '',
    peer_reviewer_id: '',
    outcome: '',
    decline_reason: '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sendBackInfo, setSendBackInfo] = useState(null); // { reason, hopName, date }

  // Pre-fill previous review notes and surface send-back reason when this is a
  // resubmission after a HoP send-back.
  // Single collection scan + client-side filter avoids Firestore composite index
  // requirement for compound where+orderBy queries on different fields.
  useEffect(() => {
    if (!deal?.id || reviewType !== 'primary_analyst') return;
    const loadPreviousReview = async () => {
      try {
        const allSnap = await getDocs(collection(db, 'deals', deal.id, 'analyst_reviews'));
        const allReviews = allSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const toMs = v => v?.toDate ? v.toDate().getTime() : new Date(v).getTime();

        // Most recent primary analyst review — pre-fill form fields
        const prevReviews = allReviews
          .filter(r => r.review_type === 'primary_analyst')
          .sort((a, b) => toMs(b.created_at) - toMs(a.created_at));
        if (prevReviews.length > 0) {
          const prev = prevReviews[0];
          setForm(f => ({
            ...f,
            notes: prev.notes || '',
            sector_concentration_flag: !!prev.sector_concentration_flag,
            sector_concentration_notes: prev.sector_concentration_notes || '',
            obligor_exposure_flag: !!prev.obligor_exposure_flag,
            obligor_exposure_notes: prev.obligor_exposure_notes || '',
            dry_powder_flag: !!prev.dry_powder_flag,
            dry_powder_notes: prev.dry_powder_notes || '',
          }));
        }

        // Most recent HoP send-back — show reason banner
        const hopSendBacks = allReviews
          .filter(r => r.review_type === 'hop_review' && r.outcome === 'send_back')
          .sort((a, b) => toMs(b.created_at) - toMs(a.created_at));
        if (hopSendBacks.length > 0) {
          const hop = hopSendBacks[0];
          setSendBackInfo({
            reason: hop.send_back_reason || 'No reason provided.',
            hopName: hop.reviewer_id || 'Head of Pipeline',
            date: hop.completed_at || hop.created_at,
          });
        }
      } catch (err) {
        console.warn('Could not load previous review:', err.message);
      }
    };
    loadPreviousReview();
  }, [deal?.id, reviewType]);

  useEffect(() => {
    if (!fund?.id) return;
    // Targeted query — only fetches users in this fund, avoiding the
    // broad getAllUsers() call that requires admin/principal permissions
    const fetchPeerReviewers = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('fund_access', 'array-contains', fund.id),
          where('is_active', '==', true)
        );
        const snap = await getDocs(q);
        const eligible = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => {
            const roles = u.roles || (u.role ? [u.role] : []);
            return roles.some(r =>
              ['analyst', 'head_of_pipeline', 'cio', 'md'].includes(r)
            ) && u.id !== user?.uid;
          });
        setAnalysts(eligible);
      } catch (err) {
        console.error('Failed to fetch peer reviewers:', err);
      }
    };
    fetchPeerReviewers();
  }, [fund, user?.uid]);

  const obligorPct = fund && deal?.requested_amount && fund.fund_size
    ? (deal.requested_amount / fund.fund_size)
    : null;

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.notes?.trim()) e.notes = 'Review notes are required';
    if (form.sector_concentration_flag && !form.sector_concentration_notes?.trim()) {
      e.sector_concentration_notes = 'Please add notes for this flag';
    }
    if (form.obligor_exposure_flag && !form.obligor_exposure_notes?.trim()) {
      e.obligor_exposure_notes = 'Please add notes for this flag';
    }
    if (form.dry_powder_flag && !form.dry_powder_notes?.trim()) {
      e.dry_powder_notes = 'Please add notes for this flag';
    }
    if (!form.outcome) e.outcome = 'Please select an outcome';
    if (form.outcome === 'advance_to_peer_review' && !form.peer_reviewer_id) {
      e.peer_reviewer_id = 'Please assign a peer reviewer';
    }
    if ((form.outcome === 'decline') && !form.decline_reason?.trim()) {
      e.decline_reason = 'Please provide a reason for declining';
    }
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      await addDoc(collection(db, 'deals', deal.id, 'analyst_reviews'), {
        deal_id: deal.id,
        review_type: reviewType,
        reviewer_id: user?.uid,
        reviewer_name: userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown',
        performed_by_name: userProfile?.full_name || userProfile?.display_name || user?.email || 'Unknown',
        pif_submission_id: deal.pif_submission_id || null,
        notes: form.notes,
        sector_concentration_flag: form.sector_concentration_flag,
        sector_concentration_notes: form.sector_concentration_notes || null,
        obligor_exposure_flag: form.obligor_exposure_flag,
        obligor_exposure_notes: form.obligor_exposure_notes || null,
        dry_powder_flag: form.dry_powder_flag,
        dry_powder_notes: form.dry_powder_notes || null,
        outcome: form.outcome,
        send_back_reason: null,
        completed_at: serverTimestamp(),
        created_at: serverTimestamp(),
      });

      if (form.outcome === 'advance_to_peer_review' || form.outcome === 'advance_to_hop_review') {
        const nextStage = form.outcome === 'advance_to_peer_review' ? 'peer_review' : 'hop_review';
        const extraUpdates = form.peer_reviewer_id
          ? { peer_reviewer_id: form.peer_reviewer_id }
          : {};

        await advanceDealStage({
          dealId: deal.id,
          newStage: nextStage,
          extraUpdates,
          performedBy: user?.uid,
          performedByName: userProfile?.full_name || userProfile?.display_name || null,
          action: reviewType === 'primary_analyst' ? 'analyst_review_completed' : 'peer_review_completed',
        });

        // Notify next reviewer
        const notifyId = form.peer_reviewer_id || null;
        if (notifyId) {
          const peer = analysts.find(a => a.id === notifyId);
          if (peer?.email) {
            await sendInternalNotification({
              recipientIds: [notifyId],
              subject: `Peer Review Required — ${deal.company_name}`,
              body: `You have been assigned as peer reviewer for ${deal.company_name}. Please log in to the portal to complete your review.`,
              dealId: deal.id,
            }).catch(() => {});
          }
        }
      } else if (form.outcome === 'decline') {
        await denyDeal({
          dealId: deal.id,
          reason: form.decline_reason,
          performedBy: user?.uid,
          performedByName: userProfile?.full_name || userProfile?.display_name || null,
          stageAtTime: deal.current_stage,
        });
        // Queue company notification
        const { sendCompanyNotification } = await import('../../services/pifService');
        await sendCompanyNotification({
          dealId: deal.id,
          notificationType: 'not_advancing',
          additionalData: {},
        }).catch(() => {});
      }

      setSuccess(true);
      setTimeout(() => onSaved?.(), 2000);
    } catch (err) {
      setErrors({ _global: getFriendlyError(err) });
    } finally {
      setSaving(false);
    }
  }

  const isPeerReview = reviewType === 'peer_review';
  const outcomeOptions = isPeerReview
    ? [
      { value: 'advance_to_hop_review', label: 'Advance to Head of Pipeline Review' },
      { value: 'decline', label: 'Decline — Do Not Advance' },
    ]
    : [
      { value: 'advance_to_peer_review', label: 'Advance to Peer Review' },
      { value: 'decline', label: 'Decline — Do Not Advance' },
    ];

  const analystOptions = analysts.map(a => ({ value: a.id, label: a.full_name }));

  // Guard: if user auth is not yet available, render nothing rather than crash on user.uid
  if (!user) return null;

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {success && <div className="alert alert-success">Review submitted successfully!</div>}

      {/* Send-back banner: shown when a HoP has returned this deal */}
      {sendBackInfo && (
        <div style={{
          background: '#fff8e6',
          border: '1px solid #c9952a',
          borderRadius: 'var(--radius)',
          padding: '14px 16px',
          marginBottom: 20,
        }}>
          <p style={{ fontWeight: 700, fontSize: 14, color: '#7a5a00', marginBottom: 4 }}>
            ⚠ This deal was sent back{sendBackInfo.date ? ` on ${formatDateTime(sendBackInfo.date)}` : ''}
          </p>
          <p style={{ fontSize: 14, color: '#7a5a00', lineHeight: 1.5 }}>
            <strong>Reason:</strong> {sendBackInfo.reason}
          </p>
          <p style={{ fontSize: 12, color: '#a07020', marginTop: 6 }}>
            Your previous notes and flags have been pre-filled. Please revise as needed and resubmit.
          </p>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">
          <h2>{isPeerReview ? 'Peer Review Notes' : 'Analyst Review Notes'}</h2>
        </div>
        <div className="card-body">
          <FormField label="Review Notes" required error={errors.notes}>
            <textarea
              className={`form-textarea ${errors.notes ? 'error' : ''}`}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={6}
              placeholder="Enter your full assessment and notes…"
              style={{ minHeight: 140 }}
            />
          </FormField>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Risk Flags</h2></div>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <ToggleField
                label="Sector Concentration Risk"
                checked={form.sector_concentration_flag}
                onChange={v => set('sector_concentration_flag', v)}
              />
              {form.sector_concentration_flag && (
                <FormField label="Concentration Notes" required error={errors.sector_concentration_notes} className="mt-2">
                  <textarea className={`form-textarea ${errors.sector_concentration_notes ? 'error' : ''}`}
                    value={form.sector_concentration_notes}
                    onChange={e => set('sector_concentration_notes', e.target.value)} rows={2} />
                </FormField>
              )}
            </div>

            <div>
              <ToggleField
                label="Single Obligor Exposure"
                checked={form.obligor_exposure_flag}
                onChange={v => set('obligor_exposure_flag', v)}
                hint={obligorPct != null ? `Requested amount is ${formatPercentage(obligorPct)} of fund size` : undefined}
              />
              {form.obligor_exposure_flag && (
                <FormField label="Exposure Notes" required error={errors.obligor_exposure_notes} className="mt-2">
                  <textarea className={`form-textarea ${errors.obligor_exposure_notes ? 'error' : ''}`}
                    value={form.obligor_exposure_notes}
                    onChange={e => set('obligor_exposure_notes', e.target.value)} rows={2} />
                </FormField>
              )}
            </div>

            <div>
              <ToggleField
                label="Dry Powder Concern"
                checked={form.dry_powder_flag}
                onChange={v => set('dry_powder_flag', v)}
              />
              {form.dry_powder_flag && (
                <FormField label="Dry Powder Notes" required error={errors.dry_powder_notes} className="mt-2">
                  <textarea className={`form-textarea ${errors.dry_powder_notes ? 'error' : ''}`}
                    value={form.dry_powder_notes}
                    onChange={e => set('dry_powder_notes', e.target.value)} rows={2} />
                </FormField>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Outcome</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <SelectField
              label="Outcome"
              required
              error={errors.outcome}
              value={form.outcome}
              onChange={e => set('outcome', e.target.value)}
              options={outcomeOptions}
            />

            {form.outcome === 'advance_to_peer_review' && (
              <SelectField
                label="Assign Peer Reviewer"
                required
                error={errors.peer_reviewer_id}
                value={form.peer_reviewer_id}
                onChange={e => set('peer_reviewer_id', e.target.value)}
                options={analystOptions}
                hint="Cannot be the primary analyst"
              />
            )}

            {form.outcome === 'decline' && (
              <FormField label="Decline Reason" required error={errors.decline_reason} className="form-full">
                <textarea
                  className={`form-textarea ${errors.decline_reason ? 'error' : ''}`}
                  value={form.decline_reason}
                  onChange={e => set('decline_reason', e.target.value)}
                  rows={3}
                />
              </FormField>
            )}
          </div>
        </div>
      </div>

      <div className="flex-between">
        <div />
        <Button variant="primary" type="submit" loading={saving}>Submit Review</Button>
      </div>
    </form>
  );
}
